/*
 * UserProfileForm (FINAL VERSION WITHOUT LOGOUT API)
 * ---------------------------------------------------------------
 * - Password update → Django logs out → clear client storage → show dialog → navigate
 * - Password + Confirm Password remain side-by-side
 * - Each eye icon toggles ONLY its own field
 * - Slide-down animation remains
 * - All previous logic kept
 */

import React, { useState, useEffect } from "react";
import ThemedInput from "./common/ThemedInput";
import Button from "./Button";
import { useTheme } from "../theme/ThemeContext";
import APIHelper from "../api/APIHelper";
import { store } from "../utils/storageHelper";
import { useNavigate } from "react-router-dom";
import "../styles/UserProfile.css";

const api = new APIHelper({
    apiUrl: process.env.REACT_APP_API_URL,
    tokenUrl: process.env.REACT_APP_TOKEN_URL,
    clientId: process.env.REACT_APP_CLIENT_ID,
    clientSecret: process.env.REACT_APP_CLIENT_SECRET,
});

export default function UserProfileForm({ userDetails, showDialog, showProgress }) {
    const { theme } = useTheme();
    const navigate = useNavigate();

    const [editMode, setEditMode] = useState(false);

    // IMPORTANT: Independent visibility states
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        username: "",
        email: "",
        organization: "",
        password: "",
        confirmPassword: "",
    });

    const [errors, setErrors] = useState({});

    // ------------------------------------------
    // INIT WITH USER DETAILS
    // ------------------------------------------
    useEffect(() => {
        if (userDetails) {
            setForm((p) => ({
                ...p,
                first_name: userDetails.first_name,
                last_name: userDetails.last_name,
                username: userDetails.username,
                email: userDetails.email,
                organization: userDetails.organization,
            }));
        }
    }, [userDetails]);

    // ------------------------------------------
    // VALIDATION
    // ------------------------------------------
    const validate = () => {
        const e = {};

        if (editMode) {
            if (!/^[a-zA-Z0-9_]+$/.test(form.username))
                e.username = "Username must be alphanumeric or underscore.";

            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                e.email = "Invalid email format.";

            if (form.password) {
                if (form.password.length < 8)
                    e.password = "Password must be at least 8 characters long.";
                if (!/[A-Z]/.test(form.password))
                    e.password = "Must contain an uppercase letter.";
                if (!/[0-9]/.test(form.password))
                    e.password = "Must contain a number.";
                if (!/[!@#$%^&*(),.?\":{}|<>]/.test(form.password))
                    e.password = "Must contain a special character.";
            }

            if (form.password !== form.confirmPassword)
                e.confirmPassword = "Passwords do not match.";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // ------------------------------------------
    // UPDATE PROFILE
    // ------------------------------------------
    const handleSave = async () => {
        if (!validate()) return;

        const payload = {
            first_name: form.first_name,
            last_name: form.last_name,
            username: form.username,
            email: form.email,
        };

        const passwordChanged = !!form.password;
        if (passwordChanged) payload.password = form.password;

        try {
            showProgress(true);

            const resp = await api.apiCall("/arcee/user/update/", {
                method: "PATCH",
                body: JSON.stringify(payload),
            });

            if (!resp.ok) {
                const err = await resp.json();
                showDialog({
                    type: "error",
                    title: "Update Failed",
                    message: JSON.stringify(err, null, 2),
                });
                return;
            }

            // ------------------------------------
            // SUCCESS CASES
            // ------------------------------------
            if (passwordChanged) {
                // Django logs user out → clear client state
                store("userData", null);
                store("access_token", null);

                showDialog({
                    type: "success",
                    title: "Password Updated",
                    message: "You have been logged out. Please login again with your new password.",
                    onOk: () => navigate("/sign-in", { replace: true }),
                });

                return;
            }

            // Simple success (no password change)
            showDialog({
                type: "success",
                title: "Profile Updated",
                message: "Your profile was updated successfully.",
                onOk: () => window.location.reload(),
            });

        } catch (err) {
            showDialog({
                type: "error",
                title: "Error",
                message: err.detail || "Failed to update profile.",
            });
        } finally {
            showProgress(false);
        }
    };

    // ------------------------------------------
    // RENDER UI
    // ------------------------------------------
    const initials = (form.first_name?.[0] || "") + (form.last_name?.[0] || "");

    const profileInputStyle = editMode
    ? {
        border: `1.4px solid ${theme.primary}`,
        borderRadius: theme.buttonRadius,
        background: "transparent",
        color: theme.text,
    }
    : {
        border: "none",
        borderBottom: `1.4px solid ${theme.primary}`,
        borderRadius: "0",
        background: "#f8fafc",
        color: "#66676b",
    };

    return (
        <div className="profile-page-container">
            <div className="profile-dashboard">

                {/* 1. TOP HEADER SECTION */}
                <div className="profile-header-card glass-card">
                    <div className="premium-avatar">
                        {initials || <i className="bi bi-person" />}
                    </div>

                    <div className="profile-user-info">
                        <h2 className="profile-user-name">
                            {form.first_name} {form.last_name}
                        </h2>
                        <p className="profile-user-meta">
                            @{form.username} • {form.email}
                        </p>
                    </div>

                    {!editMode && (
                        <div className="header-actions">
                            <Button mode="primary-bold" onClick={() => setEditMode(true)}>
                                <i className="bi bi-pencil-square me-2" />
                                Edit Profile
                            </Button>
                        </div>
                    )}
                </div>

                {/* 2. MAIN CONTENT GRID */}
                <div className="dashboard-grid">

                    {/* LEFT COLUMN: PRIMARY FORM */}
                    <div className="glass-card">
                        <div className="section-header">
                            <i className="bi bi-person-lines-fill" />
                            <h4>Account Details</h4>
                        </div>

                        <div className="row g-4">
                            <div className="col-md-6 field-group">
                                <label className="field-label">First Name</label>
                                <ThemedInput
                                    disabled={!editMode}
                                    value={form.first_name}
                                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                                    style={profileInputStyle}
                                />
                            </div>
                            <div className="col-md-6 field-group">
                                <label className="field-label">Last Name</label>
                                <ThemedInput
                                    disabled={!editMode}
                                    value={form.last_name}
                                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                                    style={profileInputStyle}
                                />
                            </div>
                        </div>

                        <div className="row g-4 mt-1">
                            <div className="col-md-6 field-group">
                                <label className="field-label">Username</label>
                                <ThemedInput
                                    disabled={!editMode}
                                    value={form.username}
                                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                                    style={profileInputStyle}
                                />
                                {errors.username && <div className="text-danger small mt-1">{errors.username}</div>}
                            </div>
                            <div className="col-md-6 field-group">
                                <label className="field-label">Email Address</label>
                                <ThemedInput
                                    disabled={!editMode}
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    style={profileInputStyle}
                                />
                                {errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
                            </div>
                        </div>

                        {/* SECURITY SECTION INSET */}
                        {editMode && (
                            <div className="mt-5 border-top pt-4">
                                <div className={`section-header mb-3 cursor-pointer`} onClick={() => setShowPassword(!showPassword)}>
                                    <i className="bi bi-shield-check" />
                                    <h4 className="flex-1">Security & Password</h4>
                                    <i className={`bi bi-chevron-${showPassword ? 'up' : 'down'} ms-auto eye-icon`} />
                                </div>

                                <div className={`security-content open`}>
                                    <div className="row g-4">
                                        <div className="col-md-6 field-group">
                                            <label className="field-label">New Password</label>
                                            <div className="position-relative">
                                                <ThemedInput
                                                    disabled={!editMode}
                                                    type={showPassword ? "text" : "password"}
                                                    value={form.password}
                                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                                    placeholder="••••••••"
                                                />
                                                <span
                                                    className="position-absolute end-0 top-0 mt-2 me-3 cursor-pointer eye-icon"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    <i className={`bi bi-eye${showPassword ? '-slash' : ''}`} />
                                                </span>
                                            </div>
                                            {errors.password && <div className="text-danger small mt-1">{errors.password}</div>}
                                        </div>
                                        <div className="col-md-6 field-group">
                                            <label className="field-label">Confirm Password</label>
                                            <div className="position-relative">
                                                <ThemedInput
                                                    disabled={!editMode}
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    value={form.confirmPassword}
                                                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                                                    placeholder="••••••••"
                                                />
                                                <span
                                                    className="position-absolute end-0 top-0 mt-2 me-3 cursor-pointer eye-icon"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                >
                                                    <i className={`bi bi-eye${showConfirmPassword ? '-slash' : ''}`} />
                                                </span>
                                            </div>
                                            {errors.confirmPassword && <div className="text-danger small mt-1">{errors.confirmPassword}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {editMode && (
                            <div className="mt-5 d-flex gap-3 justify-content-end">
                                <Button mode="outlined" onClick={() => {
                                    setEditMode(false);
                                    setErrors({});
                                    setForm({ ...form, password: "", confirmPassword: "" });
                                }}>
                                    Discard Changes
                                </Button>
                                <Button mode="primary-bold" onClick={handleSave}>
                                    Save Profile Changes
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN: SUMMARY & INFO */}
                    <div className="d-flex flex-column gap-4">
                        <div className="glass-card">
                            <div className="section-header">
                                <i className="bi bi-building" />
                                <h4>Organization</h4>
                            </div>
                            <div className="org-premium-card">
                                <i className="bi bi-shield-shaded" />
                                <div className="org-premium-content">
                                    <span className="org-label">Partner Entity</span>
                                    <span className="org-name">{form.organization || "No Organization"}</span>
                                </div>
                            </div>

                            <div className="mt-4">
                                {/* <div className="summary-item">
                                    <span className="summary-label">Member Since</span>
                                    <span className="summary-value">January 2025</span>
                                </div> */}
                                <div className="summary-item">
                                    <span className="summary-label">Account Status</span>
                                    <span className="summary-value status-verified">Verified Active</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">Role Priority</span>
                                    <span className="summary-value">Administrator</span>
                                </div>
                            </div>
                        </div>

                        {/* <div className="glass-card glass-card-muted">
                            <div className="section-header mb-3">
                                <i className="bi bi-info-circle" />
                                <h4>Help & Support</h4>
                            </div>
                            <p className="text-muted small">Need to update your email or organization? Contact your system administrator for security-related identity changes.</p>
                        </div> */}
                    </div>
                </div>
            </div>
        </div>
    );
}
